import { useCallback, useEffect, useMemo, useState } from "react";

import { I18nApi, Log, TranslationBundle } from "@ryde/shared";

import { II18nKeyEntry } from "./custom-translations.models";

type ResourceBundle = TranslationBundle;

/**
 * Recursively flattens a nested i18n resource bundle into dot-separated paths,
 * keeping only leaf string values (matching i18next `keySeparator: "."`).
 */
const flattenBundle = (
  bundle: ResourceBundle | undefined,
  prefix = "",
  result: Record<string, string> = {},
): Record<string, string> => {
  if (!bundle) {
    return result;
  }

  for (const [key, value] of Object.entries(bundle)) {
    const path = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenBundle(value as ResourceBundle, path, result);
    } else if (typeof value === "string") {
      result[path] = value;
    }
  }

  return result;
};

interface IUseI18nKeyIndex {
  entries: II18nKeyEntry[];
  isLoading: boolean;
  getOriginal: (path: string) => II18nKeyEntry | undefined;
}

/**
 * Fetches the raw EN + HE bundles directly from the backend (before client-side
 * custom-translation overrides are injected) and exposes a flat, alphabetically
 * sorted index of all translation keys with their original system values. This
 * lets the user search the original path and see the true original value — not
 * the company's own override — before editing it.
 */
export const useI18nKeyIndex = (): IUseI18nKeyIndex => {
  const [isLoading, setIsLoading] = useState(true);
  const [entries, setEntries] = useState<II18nKeyEntry[]>([]);

  useEffect(() => {
    let cancelled = false;

    const buildIndex = async (): Promise<void> => {
      try {
        // The landing pages' strings are a copy of keys in this bundle, so it holds every key.
        const [enCore, heCore] = await Promise.all([
          I18nApi.getTranslationsBundle("en"),
          I18nApi.getTranslationsBundle("he"),
        ]);

        if (cancelled) {
          return;
        }

        const enBundle = flattenBundle(enCore);
        const heBundle = flattenBundle(heCore);

        const paths = new Set<string>([
          ...Object.keys(enBundle),
          ...Object.keys(heBundle),
        ]);

        const list: II18nKeyEntry[] = [...paths]
          .sort((a, b) => a.localeCompare(b))
          .map(path => ({
            path,
            en: enBundle[path] ?? "",
            he: heBundle[path] ?? "",
          }));

        setEntries(list);
      } catch (error) {
        Log.warn(
          "Failed to load language bundles for custom translations",
          error,
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void buildIndex();

    return () => {
      cancelled = true;
    };
  }, []);

  const byPath = useMemo(
    () => new Map(entries.map(entry => [entry.path, entry])),
    [entries],
  );

  const getOriginal = useCallback(
    (path: string): II18nKeyEntry | undefined => byPath.get(path),
    [byPath],
  );

  return { entries, isLoading, getOriginal };
};
